import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import { mapAdminProfileOptions } from './use-leads-list.js';
import { buildImportPreview, IMPORT_DEFAULTS, IMPORT_PAGE_SIZE, isImportActive, isImportDeletable, saveImportDownload, suggestMapping } from '../model/lead-import.model.js';

export function useLeadImport() {
  const { token, user, hasPermission } = useAuth();
  const identity = String(user?.uid || user?.id || '');
  const authenticated = Boolean(token);
  const accessToken = useRef(token);
  useEffect(() => { accessToken.current = token; }, [token]);
  const [params, setParams] = useSearchParams();
  const uid = params.get('id') || '';
  const canImport = !!hasPermission?.('lead.create') && !!hasPermission?.('lead.view');
  const canAssign = !!hasPermission?.('lead.assign');
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [managers, setManagers] = useState([]);
  const [sheet, setSheet] = useState(0);
  const [mapping, setMapping] = useState([]);
  const [defaults, setDefaults] = useState({ ...IMPORT_DEFAULTS });
  const [dirty, setDirty] = useState(false);
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [historyError, setHistoryError] = useState('');
  const [managerError, setManagerError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const historySession = useRef(0);
  useEffect(() => {
    historySession.current += 1;
    return () => { historySession.current += 1; };
  }, [identity, authenticated, canImport]);
  const generation = useRef(0);
  const operation = useRef(false);
  const initialized = useRef('');
  const dirtyRef = useRef(false);
  const job = data?.import;

  useEffect(() => {
    let cancelled = false;
    if (!canImport) return undefined;
    setHistoryLoading(true);
    adminApi.listLeadImports(accessToken.current, { offset: historyOffset, limit: 25 }).then((response) => {
      if (!cancelled) {
        setHistory(response.imports || []); setHistoryTotal(response.total || 0); setHistoryError('');
        if (historyOffset > 0 && historyOffset >= (response.total || 0)) setHistoryOffset(0);
      }
    }).catch((e) => { if (!cancelled) setHistoryError(e.message); })
      .finally(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [identity, authenticated, canImport, job?.status, uid, refresh, historyOffset, historyRefresh]);

  useEffect(() => {
    let cancelled = false;
    if (!canImport || !canAssign) return undefined;
    adminApi.getProfiles(accessToken.current, { isAdmin: true, limit: 100 }).then((response) => {
      if (!cancelled) setManagers(mapAdminProfileOptions(response));
    }).catch((e) => { if (!cancelled) setManagerError(e.message); });
    return () => { cancelled = true; };
  }, [identity, authenticated, canImport, canAssign]);

  useEffect(() => {
    generation.current += 1;
    operation.current = false;
    initialized.current = '';
    dirtyRef.current = false;
    setData(null); setError(''); setDirty(false); setOffset(0); setDeleteTarget(null);
    setSheet(0); setMapping([]); setDefaults({ ...IMPORT_DEFAULTS });
    return () => { generation.current += 1; };
  }, [uid, identity, authenticated, canImport]);

  useEffect(() => {
    if (!uid || !canImport) { setBusy(false); return undefined; }
    let cancelled = false;
    let timer;
    const current = generation.current;
    const load = async () => {
      try {
        const response = await adminApi.getLeadImport(accessToken.current, uid, { offset, limit: IMPORT_PAGE_SIZE });
        if (cancelled || current !== generation.current) return;
        setData(response); setError('');
        const revisionKey = `${uid}:${response.import.revision}`;
        if (initialized.current !== revisionKey && !dirtyRef.current) {
          const selected = response.import.sheet ?? response.sheets?.[0]?.index ?? 0;
          setSheet(selected);
          const columns = response.sheets?.find((s) => s.index === selected)?.columns || [];
          const savedMapping = response.import.mapping || [];
          setMapping(savedMapping.length
            ? columns.map((column) => ({ column: column.index, field: '', detailName: '', ...savedMapping.find((item) => item.column === column.index) }))
            : suggestMapping(columns));
          setDefaults({ ...IMPORT_DEFAULTS, ...response.import.defaults });
          initialized.current = revisionKey;
        }
        if (isImportActive(response.import.status)) timer = setTimeout(load, 2000);
      } catch (e) {
        if (!cancelled && current === generation.current) setError(e.message);
      } finally {
        if (!cancelled && current === generation.current) setBusy(false);
      }
    };
    setBusy(true); load();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [uid, identity, authenticated, canImport, offset, refresh]);

  const run = useCallback(async (action) => {
    if (operation.current || !canImport) return;
    operation.current = true;
    const current = generation.current;
    setBusy(true); setError('');
    try {
      await action(() => current === generation.current);
    } catch (e) {
      if (current === generation.current) setError(e.message || 'requestFailed');
    } finally {
      if (current === generation.current) { operation.current = false; setBusy(false); }
    }
  }, [canImport]);

  const upload = (file, encoding = 'auto') => run(async (isCurrent) => {
    if (!file || !/\.(csv|xlsx)$/i.test(file.name)) throw new Error('fileTypeError');
    if (file.size > 10 * 1024 * 1024) throw new Error('fileSizeError');
    const response = await adminApi.uploadLeadImport(token, file, encoding);
    if (isCurrent()) { setHistoryOffset(0); setParams({ id: response.import.uid }); }
  });
  const selectSheet = (value) => {
    setSheet(value); setMapping(suggestMapping(data.sheets.find((s) => s.index === value)?.columns || []));
    dirtyRef.current = true; setDirty(true);
  };
  const changeMapping = (column, changes) => {
    setMapping((items) => items.map((item) => item.column === column ? { ...item, ...changes } : item));
    dirtyRef.current = true; setDirty(true);
  };
  const changeDefault = (key, value) => { setDefaults((values) => ({ ...values, [key]: value })); dirtyRef.current = true; setDirty(true); };
  const preview = () => run(async (isCurrent) => {
    const body = buildImportPreview(sheet, mapping, defaults);
    const response = await adminApi.previewLeadImport(token, uid, body);
    if (isCurrent()) { initialized.current = `${uid}:${response.import.revision}`; dirtyRef.current = false; setData(response); setDirty(false); setOffset(0); }
  });
  const confirm = () => run(async (isCurrent) => {
    if (dirty || job?.status !== 'preview' || !job.readyCount) throw new Error('previewRequired');
    const response = await adminApi.confirmLeadImport(token, uid, job.revision);
    if (isCurrent()) { setData((previous) => ({ ...previous, import: response.import })); setRefresh((value) => value + 1); }
  });
  const download = () => run(async (isCurrent) => {
    const response = await adminApi.downloadLeadImportReport(token, uid);
    if (isCurrent()) saveImportDownload(response);
  });
  const requestDelete = (target) => {
    if (!busy && canImport && isImportDeletable(target?.status)) {
      setDeleteTarget(target); setError('');
    }
  };
  const cancelDelete = () => { if (!operation.current) setDeleteTarget(null); };
  const deleteImport = () => run(async (isCurrent) => {
    if (!deleteTarget || isImportActive(deleteTarget.status)) return;
    const deletedUid = deleteTarget.uid;
    const session = historySession.current;
    await adminApi.deleteLeadImport(accessToken.current, deletedUid);
    if (session === historySession.current) setHistoryRefresh((value) => value + 1);
    if (isCurrent()) {
      setDeleteTarget(null);
      setHistory((items) => items.filter((item) => item.uid !== deletedUid));
      setHistoryTotal((total) => Math.max(0, total - 1));
      if (deletedUid === uid) { setData(null); setParams({}); }
    }
  });
  return { uid, data, job, history, historyOffset, historyTotal, historyLoading, setHistoryOffset, managers, sheet, mapping, defaults, dirty, offset, busy, error, historyError, managerError,
    deleteTarget, requestDelete, cancelDelete, deleteImport,
    canImport, canAssign, upload, selectSheet, changeMapping, changeDefault, preview, confirm, download,
    selectImport: (id) => setParams(id ? { id } : {}), setOffset, reload: () => setRefresh((value) => value + 1),
  };
}
